import React from 'react';
import { Card, Col, Row, Spin, Statistic, Typography } from 'antd';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
  IdcardOutlined,
  ScheduleOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';

const { Text, Paragraph } = Typography;

const EssHomePage: React.FC = () => {
  const { data: summary, isLoading } = useQuery({
    queryKey: hrmsKeys.essSummary(),
    queryFn: () => hrmsService.getEssSummary(),
  });

  const emp = summary?.employee || summary;

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="My HRMS"
          subtitle="Leave, overtime, timesheets, payslips, profile changes and letter requests"
        />
      }
    >
      {isLoading ? (
        <Spin />
      ) : (
        <>
          <Paragraph type="secondary" style={{ marginTop: 0 }}>
            {emp?.full_name || 'Your HR workspace'}
            {emp?.employee_code ? ` · ${emp?.employee_code}` : ''}
          </Paragraph>
          {Array.isArray(summary?.leave_balances) && summary.leave_balances.length > 0 && (
            <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
              {summary.leave_balances.slice(0, 4).map((b: any) => (
                <Col xs={12} sm={6} key={b.id || b.leave_type}>
                  <Card size="small">
                    <Statistic
                      title={b.leave_type_name || b.leave_name || b.leave_type}
                      value={b.available_days ?? b.balance_days ?? 0}
                      suffix="days"
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          )}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Link to="/hr/me/leave">
                <Card hoverable>
                  <Statistic
                    title="Leave"
                    value={summary?.open_leave_count ?? 0}
                    suffix="open"
                    prefix={<CalendarOutlined />}
                  />
                </Card>
              </Link>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Link to="/hr/me/ot">
                <Card hoverable>
                  <Statistic
                    title="Overtime"
                    value={summary?.open_ot_count ?? 0}
                    suffix="open"
                    prefix={<ClockCircleOutlined />}
                  />
                </Card>
              </Link>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Link to="/hr/me/payslips">
                <Card hoverable>
                  <Statistic title="Payslips" value="View" prefix={<DollarOutlined />} valueStyle={{ fontSize: 20 }} />
                </Card>
              </Link>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Link to="/hr/me/profile">
                <Card hoverable>
                  <Statistic title="Profile" value="Edit" prefix={<IdcardOutlined />} valueStyle={{ fontSize: 20 }} />
                </Card>
              </Link>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Link to="/hr/me/timesheets">
                <Card hoverable>
                  <Statistic title="Timesheets" value="View" prefix={<ScheduleOutlined />} valueStyle={{ fontSize: 20 }} />
                </Card>
              </Link>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Link to="/hr/me/letters">
                <Card hoverable>
                  <Statistic title="Letters" value="Request" prefix={<FileTextOutlined />} valueStyle={{ fontSize: 20 }} />
                </Card>
              </Link>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Card>
                <Statistic
                  title="Status"
                  value={emp?.employment_status || '—'}
                  prefix={<UserOutlined />}
                  valueStyle={{ fontSize: 18 }}
                />
              </Card>
            </Col>
          </Row>
          {(emp?.job_title || emp?.department_id) && (
            <Card size="small" style={{ marginTop: 16 }}>
              <Text type="secondary">{[emp.job_title, emp.department_name].filter(Boolean).join(' · ') || '—'}</Text>
            </Card>
          )}
        </>
      )}
    </WorkspaceLayout>
  );
};

export default EssHomePage;
